import { useEffect, useMemo, useState } from "react";
import {
    Sparkles,
    ChevronDown,
    X,
    Search,
    Grid3x3,
    Rows,
    ArrowLeft,
    Tag,
    Info,
} from "lucide-react";
import { PRODUCTS } from "../lib/data";
import { api } from "../lib/api";
import { openWhatsApp } from "../lib/whatsapp";
import LazyImage from "./LazyImage";

// ---- Variant helpers ----
function uniqueColors(variants) {
    const map = new Map();
    (variants || []).forEach((v) => {
        if (v.color && !map.has(v.color))
            map.set(v.color, v.color_hex || "#CCCCCC");
    });
    return Array.from(map.entries()).map(([name, hex]) => ({ name, hex }));
}
function uniqueSizes(variants) {
    return Array.from(
        new Set((variants || []).filter((v) => v.size).map((v) => v.size)),
    );
}
function findVariant(variants, size, color) {
    if (!variants || variants.length === 0) return null;
    return (
        variants.find(
            (v) =>
                (size ? v.size === size : !v.size) &&
                (color ? v.color === color : !v.color),
        ) ||
        variants.find(
            (v) =>
                (size ? v.size === size : true) &&
                (color ? v.color === color : true),
        ) ||
        null
    );
}

/**
 * Hook that powers the variant picker (colors + sizes) and exposes the
 * currently-selected variant, the resolved price and a ready-to-send
 * WhatsApp message with the chosen options.
 */
function useVariants(product) {
    const variants = product.variants || [];
    const colors = useMemo(() => uniqueColors(variants), [variants]);
    const sizes = useMemo(() => uniqueSizes(variants), [variants]);
    const [size, setSize] = useState(sizes[0] || null);
    const [color, setColor] = useState(colors[0]?.name || null);

    // Re-sync defaults when product changes (e.g. swapping modals)
    useEffect(() => {
        setSize(sizes[0] || null);
        setColor(colors[0]?.name || null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [product.id]);

    const variant = useMemo(
        () => findVariant(variants, size, color),
        [variants, size, color],
    );

    const price = (variant && variant.price) || product.price || "";

    const baseMsg =
        product.whatsapp_message ||
        `Hola Silvia, me interesa el producto "${product.name}".`;
    const parts = [];
    if (size) parts.push(`medida ${size}`);
    if (color) parts.push(`color ${color}`);
    if (price) parts.push(`precio ${price}`);
    const msg = parts.length
        ? `${baseMsg.replace(/[.\s]+$/, "")} · ${parts.join(", ")}.`
        : baseMsg;

    return {
        colors,
        sizes,
        size,
        color,
        setSize,
        setColor,
        price,
        msg,
        hasVariants: variants.length > 0,
    };
}

function VariantPicker({
    colors,
    sizes,
    size,
    color,
    setSize,
    setColor,
    compact = false,
    testIdPrefix = "card",
}) {
    if (colors.length === 0 && sizes.length === 0) return null;
    return (
        <div
            className={compact ? "mt-2 space-y-2" : "mt-4 space-y-3"}
            onClick={(e) => e.stopPropagation()}
        >
            {colors.length > 0 && (
                <div>
                    <p
                        className={`text-[9px] tracking-[0.28em] uppercase text-[#9a958c] ${
                            compact ? "mb-1" : "mb-1.5"
                        }`}
                    >
                        Color: <span className="text-[#1C1A18]">{color}</span>
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                        {colors.map((c) => (
                            <button
                                type="button"
                                key={c.name}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setColor(c.name);
                                }}
                                aria-label={`Color ${c.name}`}
                                title={c.name}
                                data-testid={`${testIdPrefix}-color-${c.name.toLowerCase()}`}
                                className={`relative rounded-full transition-all ${
                                    compact ? "w-5 h-5" : "w-6 h-6"
                                } ${
                                    color === c.name
                                        ? "ring-2 ring-offset-1 ring-[#1C1A18]"
                                        : "ring-1 ring-[#e6e2db] hover:ring-[#1C1A18]"
                                }`}
                                style={{ backgroundColor: c.hex }}
                            />
                        ))}
                    </div>
                </div>
            )}
            {sizes.length > 0 && (
                <div>
                    <p
                        className={`text-[9px] tracking-[0.28em] uppercase text-[#9a958c] ${
                            compact ? "mb-1" : "mb-1.5"
                        }`}
                    >
                        Medida
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                        {sizes.map((s) => (
                            <button
                                type="button"
                                key={s}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setSize(s);
                                }}
                                data-testid={`${testIdPrefix}-size-${s.toLowerCase()}`}
                                className={`px-2.5 py-1 text-[10px] tracking-wider border transition-colors ${
                                    size === s
                                        ? "bg-[#1C1A18] border-[#1C1A18] text-white"
                                        : "bg-white border-[#e6e2db] text-[#1C1A18] hover:border-[#1C1A18]"
                                }`}
                            >
                                {s}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

export default function Products() {
    const [items, setItems] = useState(PRODUCTS);
    const [open, setOpen] = useState(false);
    const [view, setView] = useState("grid"); // grid | scroll
    const [query, setQuery] = useState("");
    const [activeCat, setActiveCat] = useState("Todo");
    const [detail, setDetail] = useState(null);

    useEffect(() => {
        let mounted = true;
        const fetchItems = () => {
            api.get(`/products?t=${Date.now()}`)
                .then(({ data }) => {
                    if (mounted && Array.isArray(data)) {
                        setItems(data);
                        requestAnimationFrame(() =>
                            window.dispatchEvent(new Event("content:ready")),
                        );
                    }
                })
                .catch(() => {});
        };
        fetchItems();
        const onVis = () => {
            if (document.visibilityState === "visible") fetchItems();
        };
        document.addEventListener("visibilitychange", onVis);
        window.addEventListener("focus", fetchItems);
        return () => {
            mounted = false;
            document.removeEventListener("visibilitychange", onVis);
            window.removeEventListener("focus", fetchItems);
        };
    }, []);

    const categories = useMemo(() => {
        const set = new Set(["Todo"]);
        items.forEach((p) => p.category && set.add(p.category));
        return Array.from(set);
    }, [items]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return items.filter((p) => {
            const catOk = activeCat === "Todo" || p.category === activeCat;
            if (!catOk) return false;
            if (!q) return true;
            return (
                p.name?.toLowerCase().includes(q) ||
                p.description?.toLowerCase().includes(q) ||
                p.category?.toLowerCase().includes(q)
            );
        });
    }, [items, query, activeCat]);

    const toggle = () => {
        setOpen((v) => {
            const next = !v;
            if (next) {
                setTimeout(() => {
                    const el = document.getElementById("productos-grid");
                    if (el)
                        el.scrollIntoView({
                            behavior: "smooth",
                            block: "start",
                        });
                }, 220);
            }
            return next;
        });
    };

    return (
        <section
            id="productos"
            data-testid="products-section"
            className="relative py-14 sm:py-20 bg-[#FAFAF8]"
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-12">
                <div className="text-center max-w-3xl mx-auto reveal">
                    <span className="text-[10px] tracking-[0.4em] uppercase text-[#C4A882]">
                        Productos
                    </span>
                    <h2
                        className="font-display text-4xl sm:text-5xl lg:text-6xl font-light text-[#1C1A18] mt-4 leading-[1.1]"
                        data-testid="products-title"
                    >
                        Tus fotos, convertidas en algo que puedes tocar
                    </h2>
                    <p
                        className="mt-6 text-[#6b665f] font-light"
                        data-testid="products-subtitle"
                    >
                        Diseñamos productos personalizados con tus fotos en
                        Bilbao: álbumes, lienzos, fotomurales y regalos
                        únicos. La compra es presencial — pulsa cualquier
                        producto y te atenderemos personalmente por WhatsApp.
                    </p>
                </div>

                {/* Botón llamativo desplegable */}
                <div className="mt-10 sm:mt-14 flex justify-center reveal reveal-delay-1">
                    <button
                        type="button"
                        onClick={toggle}
                        aria-expanded={open}
                        aria-controls="productos-grid"
                        data-testid="products-toggle-btn"
                        className="group relative inline-flex items-center gap-4 px-8 sm:px-12 py-5 sm:py-6 bg-[#1C1A18] text-[#FAFAF8] rounded-full overflow-hidden transition-all duration-500 hover:bg-[#C4A882] hover:text-[#1C1A18] active:scale-95 shadow-2xl"
                    >
                        <Sparkles
                            size={20}
                            strokeWidth={1.2}
                            className={`text-[#C4A882] group-hover:text-[#1C1A18] transition-transform duration-500 ${
                                open ? "rotate-180" : ""
                            }`}
                        />
                        <span className="flex flex-col items-start text-left">
                            <span className="text-[9px] sm:text-[10px] tracking-[0.4em] uppercase opacity-70">
                                {open ? "Cerrar tienda" : "Descubre"}
                            </span>
                            <span className="font-display text-xl sm:text-2xl tracking-wide leading-none mt-1">
                                {open ? "Cerrar productos" : "Ver tienda"}
                            </span>
                        </span>
                        {open ? (
                            <X
                                size={20}
                                strokeWidth={1.2}
                                className="text-[#C4A882] group-hover:text-[#1C1A18] transition-colors"
                            />
                        ) : (
                            <ChevronDown
                                size={20}
                                strokeWidth={1.2}
                                className="text-[#C4A882] group-hover:text-[#1C1A18] transition-transform duration-500 group-hover:translate-y-1"
                            />
                        )}
                    </button>
                </div>

                {!open && (
                    <p className="mt-4 text-center text-[10px] tracking-[0.32em] uppercase text-[#9a958c]">
                        {items.length} productos disponibles
                    </p>
                )}

                {/* Tienda colapsable */}
                <div
                    id="productos-grid"
                    className={`grid transition-[grid-template-rows,opacity] duration-700 ease-out ${
                        open
                            ? "grid-rows-[1fr] opacity-100 mt-12"
                            : "grid-rows-[0fr] opacity-0 mt-0"
                    }`}
                    aria-hidden={!open}
                >
                    <div className="overflow-hidden">
                        {/* Barra de búsqueda + view toggle */}
                        <div className="mb-8 flex flex-col gap-4">
                            <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center justify-between">
                                <div className="relative flex-1 max-w-xl">
                                    <Search
                                        size={16}
                                        strokeWidth={1.4}
                                        className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9a958c]"
                                    />
                                    <input
                                        type="search"
                                        value={query}
                                        onChange={(e) =>
                                            setQuery(e.target.value)
                                        }
                                        placeholder="Buscar producto…"
                                        data-testid="products-search"
                                        className="w-full pl-11 pr-4 py-3 rounded-full bg-white border border-[#e6e2db] text-sm text-[#1C1A18] font-light focus:outline-none focus:border-[#C4A882] transition-colors"
                                    />
                                </div>
                                <div className="inline-flex items-center bg-white border border-[#e6e2db] rounded-full p-1 self-start sm:self-auto">
                                    <button
                                        type="button"
                                        onClick={() => setView("grid")}
                                        aria-pressed={view === "grid"}
                                        data-testid="products-view-grid"
                                        className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-[10px] tracking-[0.28em] uppercase transition-all ${
                                            view === "grid"
                                                ? "bg-[#1C1A18] text-white"
                                                : "text-[#6b665f]"
                                        }`}
                                    >
                                        <Grid3x3 size={14} strokeWidth={1.4} />
                                        Cuadrícula
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setView("scroll")}
                                        aria-pressed={view === "scroll"}
                                        data-testid="products-view-scroll"
                                        className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-[10px] tracking-[0.28em] uppercase transition-all ${
                                            view === "scroll"
                                                ? "bg-[#1C1A18] text-white"
                                                : "text-[#6b665f]"
                                        }`}
                                    >
                                        <Rows size={14} strokeWidth={1.4} />
                                        Uno a uno
                                    </button>
                                </div>
                            </div>

                            {/* Filtros por categoría */}
                            {categories.length > 2 && (
                                <div
                                    className="flex flex-wrap gap-x-6 gap-y-2"
                                    data-testid="products-categories"
                                >
                                    {categories.map((c) => (
                                        <button
                                            key={c}
                                            type="button"
                                            onClick={() => setActiveCat(c)}
                                            className={`chip ${
                                                activeCat === c ? "active" : ""
                                            }`}
                                            data-testid={`products-cat-${c.toLowerCase()}`}
                                        >
                                            {c}
                                        </button>
                                    ))}
                                </div>
                            )}

                            <p className="text-[10px] tracking-[0.32em] uppercase text-[#9a958c]">
                                {filtered.length} resultado
                                {filtered.length === 1 ? "" : "s"}
                            </p>
                        </div>

                        {filtered.length === 0 ? (
                            <p className="text-center py-16 text-sm text-[#6b665f]">
                                No hay productos que coincidan con tu búsqueda.
                            </p>
                        ) : view === "grid" ? (
                            <div
                                className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-5 lg:gap-7"
                                data-testid="products-grid"
                            >
                                {filtered.map((p) => (
                                    <ProductCard
                                        key={p.id}
                                        product={p}
                                        onOpen={() => setDetail(p)}
                                    />
                                ))}
                            </div>
                        ) : (
                            <div
                                className="space-y-6"
                                data-testid="products-list"
                            >
                                {filtered.map((p) => (
                                    <ProductRow
                                        key={p.id}
                                        product={p}
                                        onOpen={() => setDetail(p)}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Detalle producto */}
            {detail && (
                <ProductDetail
                    product={detail}
                    onClose={() => setDetail(null)}
                />
            )}
        </section>
    );
}

function ProductCard({ product, onOpen }) {
    const { colors, sizes, size, color, setSize, setColor, price, msg, hasVariants } =
        useVariants(product);
    return (
        <article
            data-testid={`product-card-${product.id}`}
            className="group bg-white overflow-hidden flex flex-col rounded-2xl shadow-sm hover:shadow-xl transition-shadow duration-500 cursor-pointer"
            onClick={onOpen}
        >
            <div className="relative aspect-square sm:aspect-[4/5] overflow-hidden">
                <LazyImage
                    src={product.image}
                    alt={`${product.name} personalizado con tus fotos - Silvia García Fotografía Bilbao`}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                {product.badge !== "" && (
                    <span className="absolute top-2 left-2 sm:top-3 sm:left-3 bg-[#C4A882] text-white text-[8px] sm:text-[9px] uppercase tracking-[0.18em] sm:tracking-[0.24em] px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full shadow">
                        {product.badge || "Personalizable"}
                    </span>
                )}
                {price && (
                    <span
                        className="absolute bottom-2 right-2 sm:bottom-3 sm:right-3 bg-[#1C1A18] text-white text-[10px] sm:text-xs font-medium tracking-wide px-2 sm:px-3 py-1 sm:py-1.5 rounded-full shadow"
                        data-testid={`product-card-price-${product.id}`}
                    >
                        {price}
                    </span>
                )}
            </div>
            <div className="p-3 sm:p-5 flex flex-col flex-grow">
                <h3 className="font-display text-sm sm:text-lg text-[#1C1A18] leading-tight">
                    {product.name}
                </h3>
                {product.category && (
                    <p className="text-[8px] sm:text-[10px] tracking-[0.2em] sm:tracking-[0.28em] uppercase text-[#9a958c] mt-1">
                        {product.category}
                    </p>
                )}
                <p className="hidden sm:block mt-3 text-xs text-[#6b665f] font-light leading-relaxed line-clamp-2">
                    {product.description}
                </p>

                {hasVariants && (
                    <VariantPicker
                        colors={colors}
                        sizes={sizes}
                        size={size}
                        color={color}
                        setSize={setSize}
                        setColor={setColor}
                        compact
                        testIdPrefix={`card-${product.id}`}
                    />
                )}

                <div className="mt-3 sm:mt-4 flex items-center gap-2">
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            openWhatsApp(msg);
                        }}
                        data-testid={`product-card-wa-${product.id}`}
                        className="flex-1 inline-flex items-center justify-center gap-1.5 px-2.5 sm:px-3 py-2 sm:py-2.5 rounded-full bg-[#25D366] text-white text-[9px] sm:text-[10px] uppercase tracking-[0.18em] sm:tracking-[0.22em] hover:bg-[#1ebd5b] active:scale-95 transition-all"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="12"
                            height="12"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            aria-hidden="true"
                        >
                            <path d="M19.077 4.928A9.94 9.94 0 0012.012 2C6.486 2 1.989 6.494 1.985 12.018a9.94 9.94 0 001.337 4.998L2 22l5.13-1.345a9.97 9.97 0 004.778 1.218h.004c5.526 0 10.022-4.494 10.026-10.018a9.94 9.94 0 00-2.861-7.927z" />
                        </svg>
                        Comprar
                    </button>
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            onOpen();
                        }}
                        data-testid={`product-view-${product.id}`}
                        aria-label="Ver detalle"
                        className="w-9 h-9 inline-flex items-center justify-center rounded-full bg-[#1C1A18] text-white hover:bg-[#C4A882] hover:text-[#1C1A18] active:scale-95 transition-all"
                    >
                        <Info size={13} strokeWidth={1.6} />
                    </button>
                </div>
            </div>
        </article>
    );
}

function ProductRow({ product, onOpen }) {
    const { colors, sizes, size, color, setSize, setColor, price, msg, hasVariants } =
        useVariants(product);
    return (
        <article
            data-testid={`product-row-${product.id}`}
            className="group flex flex-col sm:flex-row gap-5 bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-shadow duration-500 cursor-pointer"
            onClick={onOpen}
        >
            <div className="relative w-full sm:w-72 aspect-[4/5] sm:aspect-auto shrink-0 overflow-hidden">
                <LazyImage
                    src={product.image}
                    alt={`${product.name} personalizado en Bilbao - Silvia García Fotografía`}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                {product.badge !== "" && (
                    <span className="absolute top-3 left-3 bg-[#C4A882] text-white text-[9px] uppercase tracking-[0.24em] px-2.5 py-1 rounded-full shadow">
                        {product.badge || "Personalizable"}
                    </span>
                )}
            </div>
            <div className="flex-1 p-6 sm:p-8 flex flex-col">
                <div className="flex flex-wrap items-baseline justify-between gap-3">
                    <h3 className="font-display text-2xl sm:text-3xl text-[#1C1A18]">
                        {product.name}
                    </h3>
                    {price && (
                        <span
                            className="text-base text-[#C4A882] font-medium tracking-wide"
                            data-testid={`product-row-price-${product.id}`}
                        >
                            {price}
                        </span>
                    )}
                </div>
                {product.category && (
                    <p className="text-[10px] tracking-[0.28em] uppercase text-[#9a958c] mt-1">
                        <Tag size={10} strokeWidth={1.6} className="inline mr-1" />
                        {product.category}
                    </p>
                )}
                <p className="mt-4 text-sm text-[#6b665f] font-light leading-relaxed flex-grow">
                    {product.description}
                </p>

                {hasVariants && (
                    <VariantPicker
                        colors={colors}
                        sizes={sizes}
                        size={size}
                        color={color}
                        setSize={setSize}
                        setColor={setColor}
                        testIdPrefix={`row-${product.id}`}
                    />
                )}

                <div className="mt-6 flex flex-wrap gap-3">
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            openWhatsApp(msg);
                        }}
                        data-testid={`product-row-wa-${product.id}`}
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#25D366] text-white text-[10px] uppercase tracking-[0.22em] hover:bg-[#1ebd5b] active:scale-95 transition-all"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            aria-hidden="true"
                        >
                            <path d="M19.077 4.928A9.94 9.94 0 0012.012 2C6.486 2 1.989 6.494 1.985 12.018a9.94 9.94 0 001.337 4.998L2 22l5.13-1.345a9.97 9.97 0 004.778 1.218h.004c5.526 0 10.022-4.494 10.026-10.018a9.94 9.94 0 00-2.861-7.927z" />
                        </svg>
                        Comprar por WhatsApp
                    </button>
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            onOpen();
                        }}
                        data-testid={`product-row-view-${product.id}`}
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1C1A18] text-white text-[10px] uppercase tracking-[0.22em] hover:bg-[#C4A882] hover:text-[#1C1A18] active:scale-95 transition-all"
                    >
                        <Info size={12} strokeWidth={1.6} /> Ver detalle
                    </button>
                </div>
            </div>
        </article>
    );
}

function ProductDetail({ product, onClose }) {
    const { colors, sizes, size, color, setSize, setColor, price, msg, hasVariants } =
        useVariants(product);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") onClose();
        };
        document.addEventListener("keydown", onKey);
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = "";
        };
    }, [onClose]);

    return (
        <div
            role="dialog"
            aria-modal="true"
            data-testid="product-detail-modal"
            className="fixed inset-0 z-[80] bg-[#1C1A18]/85 flex items-start sm:items-center justify-center p-0 sm:p-6 overflow-y-auto"
            onClick={onClose}
        >
            <div
                className="bg-[#FAFAF8] w-full max-w-4xl rounded-none sm:rounded-3xl overflow-hidden shadow-2xl my-0 sm:my-12 grid grid-cols-1 md:grid-cols-2"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="relative aspect-square md:aspect-auto md:min-h-[480px] bg-[#f0ece4]">
                    <LazyImage
                        src={product.image}
                        alt={`${product.name} - Producto personalizado con fotos Bilbao | Silvia García`}
                        eager
                        className="w-full h-full object-cover"
                    />
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="product-detail-close"
                        aria-label="Cerrar"
                        className="absolute top-4 left-4 md:hidden inline-flex items-center gap-2 px-3 py-2 bg-white/90 rounded-full text-[#1C1A18] text-xs"
                    >
                        <ArrowLeft size={14} /> Volver
                    </button>
                    {product.badge !== "" && (
                        <span className="absolute top-4 right-4 bg-[#C4A882] text-white text-[9px] uppercase tracking-[0.24em] px-3 py-1.5 rounded-full shadow">
                            {product.badge || "Personalizable"}
                        </span>
                    )}
                </div>
                <div className="p-7 sm:p-10 flex flex-col">
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Cerrar"
                        data-testid="product-detail-close-desktop"
                        className="hidden md:inline-flex absolute top-5 right-5 w-9 h-9 items-center justify-center rounded-full bg-white text-[#1C1A18] hover:bg-[#1C1A18] hover:text-white transition-colors"
                    >
                        <X size={16} />
                    </button>
                    {product.category && (
                        <span className="text-[10px] tracking-[0.4em] uppercase text-[#C4A882] mb-3">
                            {product.category}
                        </span>
                    )}
                    <h3 className="font-display text-3xl sm:text-4xl text-[#1C1A18] leading-tight">
                        {product.name}
                    </h3>
                    {price && (
                        <div
                            className="mt-4 inline-flex items-center gap-2 text-2xl font-medium text-[#1C1A18]"
                            data-testid={`product-detail-price-${product.id}`}
                        >
                            <Tag
                                size={16}
                                strokeWidth={1.6}
                                className="text-[#C4A882]"
                            />
                            {price}
                        </div>
                    )}
                    <p className="mt-6 text-[15px] text-[#3b3833] font-light leading-relaxed">
                        {product.description}
                    </p>

                    {hasVariants && (
                        <VariantPicker
                            colors={colors}
                            sizes={sizes}
                            size={size}
                            color={color}
                            setSize={setSize}
                            setColor={setColor}
                            testIdPrefix={`detail-${product.id}`}
                        />
                    )}

                    <div className="mt-8 p-5 rounded-2xl bg-white border border-[#e6e2db] text-sm text-[#6b665f] font-light">
                        <p className="text-[10px] tracking-[0.4em] uppercase text-[#C4A882] mb-2">
                            Compra presencial
                        </p>
                        Cada producto se diseña a medida. Contacta por WhatsApp
                        y te atendemos personalmente para mostrarte muestras,
                        opciones y plazos.
                    </div>

                    <button
                        type="button"
                        onClick={() => openWhatsApp(msg)}
                        data-testid="product-detail-whatsapp"
                        className="mt-8 inline-flex items-center justify-center gap-3 px-8 py-4 btn-wa text-[11px] uppercase rounded-full active:scale-95"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="-1 -1 26 26"
                            fill="currentColor"
                            aria-hidden="true"
                            style={{ display: "block" }}
                        >
                            <path d="M19.077 4.928A9.94 9.94 0 0012.012 2C6.486 2 1.989 6.494 1.985 12.018a9.94 9.94 0 001.337 4.998L2 22l5.13-1.345a9.97 9.97 0 004.778 1.218h.004c5.526 0 10.022-4.494 10.026-10.018a9.94 9.94 0 00-2.861-7.927zM12.012 20.18h-.003a8.31 8.31 0 01-4.234-1.158l-.304-.18-3.045.799.812-2.967-.198-.314a8.27 8.27 0 01-1.27-4.412c.002-4.598 3.745-8.34 8.347-8.34 2.228 0 4.323.87 5.898 2.448a8.28 8.28 0 012.443 5.896c-.002 4.598-3.745 8.34-8.346 8.34zm4.578-6.247c-.25-.125-1.486-.733-1.716-.817-.23-.084-.398-.125-.566.125-.167.25-.65.817-.797.984-.146.168-.293.188-.543.063-.25-.125-1.058-.39-2.016-1.245-.745-.665-1.248-1.485-1.395-1.735-.146-.25-.016-.385.11-.51.112-.112.25-.293.376-.439.125-.146.166-.25.25-.418.084-.167.042-.313-.02-.438-.063-.125-.566-1.364-.776-1.867-.205-.49-.412-.424-.566-.432-.146-.007-.314-.009-.481-.009a.93.93 0 00-.67.314c-.231.25-.881.86-.881 2.099 0 1.24.902 2.436 1.028 2.604.125.167 1.776 2.713 4.302 3.804.6.26 1.07.413 1.435.529.604.192 1.153.165 1.587.1.484-.072 1.486-.608 1.696-1.195.21-.586.21-1.089.147-1.194-.063-.105-.23-.168-.48-.293z" />
                        </svg>
                        Pedir información por WhatsApp
                    </button>
                </div>
            </div>
        </div>
    );
}
